<template>
  <div>
    <base-header class="pb-6">
      <template #title>
        <div class="d-flex align-items-center">
          <h1 class="text-white mb-0">Update Schedules</h1>
        </div>
      </template>
     
    </base-header>

    <b-container fluid class="mt--6">
      <!-- Schedule table card -->
      <b-card no-body class="shadow">
        <!-- Card header with tabs -->
       <template #header>
          <div class="d-flex justify-content-between align-items-center flex-wrap" style="gap: 16px;">
            <!-- Left: Status filter tabs -->
            <div class="d-flex" style="gap: 2px;">
              <button
                v-for="tab in statusTabs"
                :key="tab.value"
                type="button"
                class="btn tab-btn"
                :class="{ 'active': statusFilter === tab.value }"
                @click="statusFilter = tab.value; loadSchedules()"
              >
                {{ tab.label }}
                <span v-if="tab.count > 0" class="badge ml-2" :class="`badge-${tab.variant}`">
                  {{ tab.count }}
                </span>
              </button>
            </div>

            <!-- Right: Date range filter + Action buttons -->
            <div class="d-flex align-items-center" style="gap: 8px;">
              <!-- Date Range Dropdown -->
              <b-dropdown
                variant="outline-secondary"
                size="sm"
                text="Date Range"
                class="mr-1"
              >
                <template #button-content>
                  <i class="fas fa-calendar-alt mr-1"></i>
                  {{ dateFilter ? dateRangeLabel : 'Date Range' }}
                </template>
                <b-dropdown-item
                  v-for="option in dateRangeOptions"
                  :key="option.value"
                  @click="setDateFilter(option.value)"
                  :active="dateFilter === option.value"
                >
                  {{ option.text }}
                </b-dropdown-item>
                <b-dropdown-divider></b-dropdown-divider>
                <b-dropdown-item
                  @click="setDateFilter(null)"
                  :disabled="!dateFilter"
                >
                  <i class="fas fa-times mr-1"></i>Clear Filter
                </b-dropdown-item>
              </b-dropdown>

              <!-- Action buttons -->
              <base-button size="sm" type="primary" @click="openCreateModal">
                <i class="fas fa-calendar-plus mr-1"></i>New Schedule
              </base-button>
              <base-button size="sm" type="outline-danger" @click="confirmCleanup">
                <i class="fas fa-trash-alt mr-1"></i>Clear Old Schedules
              </base-button>
            </div>
          </div>
        </template>

        <!-- Table body -->
        <b-card-body class="p-0">
          <b-table
            :items="displayedSchedules"
            :fields="tableFields"
            :busy="loading"
            hover
            responsive
            show-empty
            empty-text="No schedules found"
          >
            <template #table-busy>
              <div class="text-center py-4">
                <b-spinner variant="primary" />
              </div>
            </template>

            <template #cell(type)="{ item }">
              <b-badge :variant="item.type === 'ota' ? 'info' : 'primary'">
                {{ item.type === 'ota' ? 'OTA' : 'Provision' }}
              </b-badge>
            </template>

            <template #cell(mode)="{ item }">
              <b-badge :variant="item.mode === 'auto' ? 'success' : 'secondary'">
                <i :class="item.mode === 'auto' ? 'fas fa-bolt' : 'fas fa-hand-pointer'" class="mr-1"></i>
                {{ item.mode }}
              </b-badge>
            </template>

            <template #cell(progress)="{ item }">
              <div v-if="item.summary.total > 0">
                <div class="d-flex justify-content-between mb-1">
                  <small class="text-muted">{{ item.summary.completed }}/{{ item.summary.total }}</small>
                  <small v-if="item.summary.needs_attention > 0" class="text-warning">
                    <i class="fas fa-exclamation-triangle mr-1"></i>{{ item.summary.needs_attention }} need attention
                  </small>
                </div>
                <b-progress height="6px" :max="item.summary.total">
                  <b-progress-bar :value="item.summary.completed" variant="success" />
                  <b-progress-bar
                    :value="item.summary.in_progress"
                    :variant="item.status === 'cancelled' ? 'secondary' : 'primary'"
                    :animated="item.status !== 'cancelled'"
                  />
                  <b-progress-bar :value="item.summary.failed + item.summary.expired" variant="danger" />
                </b-progress>
              </div>
              <small v-else class="text-muted">No devices yet</small>
            </template>

            <template #cell(status)="{ item }">
              <b-badge :variant="statusVariant(item.status)">{{ item.status }}</b-badge>
            </template>

            <template #cell(created_at)="{ item }">
              <small>{{ formatDate(item.created_at) }}</small>
            </template>

            <template #cell(actions)="{ item }">
              <div class="d-flex" style="gap:6px;">
                <base-button size="sm" type="outline-primary" @click="viewSchedule(item.id)">
                  <i class="fas fa-eye"></i>
                </base-button>
                <base-button
                  v-if="item.status === 'active'"
                  size="sm"
                  type="outline-secondary"
                  @click="openEditModal(item)"
                >
                  <i class="fas fa-edit"></i>
                </base-button>
                <base-button
                  v-if="item.status === 'active'"
                  size="sm"
                  type="outline-danger"
                  @click="confirmCancel(item)"
                >
                  <i class="fas fa-ban"></i>
                </base-button>
                <!-- Delete button for non-active schedules -->
                <base-button
                  v-if="item.status !== 'active'"
                  size="sm"
                  type="outline-danger"
                  @click="confirmDeleteSchedule(item)"
                >
                  <i class="fas fa-trash-alt"></i>
                </base-button>
              </div>
            </template>
          </b-table>
        </b-card-body>
      </b-card>

      <modal-schedule-create
        ref="createModal"
        @created="onScheduleCreated"
        @updated="onScheduleUpdated"
      />
    </b-container>
  </div>
</template>

<script>
import ModalScheduleCreate from '@/views/components/ModalScheduleCreate';

export default {
  name: 'ScheduleList',
  components: { ModalScheduleCreate },
  data() {
    return {
      loading: false,
      statusFilter: 'all',
       pollTimer: null,   
        dateFilter: null,                 // current selected date range value
    dateRangeOptions: [               // options from DeviceList's filter config
      { value: '24_hours', text: 'Last 24 Hours' },
      { value: '3_days', text: 'Last 3 Days' },
      { value: 'week', text: 'Last Week' },
      { value: 'month', text: 'Last Month' },
      { value: '3_months', text: 'Last 3 Months' },
      { value: '6_months', text: 'Last 6 Months' },
      { value: 'year', text: 'Last Year' },
    ],
      tableFields: [
        { key: 'name', label: 'Name', sortable: true },
        { key: 'type', label: 'Type' },
        { key: 'mode', label: 'Mode' },
        { key: 'progress', label: 'Progress' },
        { key: 'status', label: 'Status' },
        { key: 'deadline_days', label: 'Deadline (days)' },
        { key: 'created_at', label: 'Created' },
        { key: 'actions', label: '' },
      ],
    };
  },
  computed: {
    schedules() {
      return this.$store.getters.schedules || [];
    },
    summary() {
      return this.$store.getters.scheduleSummary || {};
    },
    statusTabs() {
      return [
          { value: 'all', label: 'All', variant: 'secondary', count: 0 },
        { value: 'active', label: 'Active', variant: 'primary', count: this.summary.active_schedules || 0 },
        { value: 'completed', label: 'Completed', variant: 'success', count: 0 },
        { value: 'cancelled', label: 'Cancelled', variant: 'secondary', count: 0 },
      ];
    },
      dateRangeLabel() {
    const opt = this.dateRangeOptions.find(o => o.value === this.dateFilter);
    return opt ? opt.text : '';
  },
  displayedSchedules() {
    if (!this.dateFilter) return this.schedules;
    const now = new Date();
    let startDate = null;
    switch (this.dateFilter) {
      case '24_hours': startDate = new Date(now - 24 * 60 * 60 * 1000); break;
      case '3_days':   startDate = new Date(now - 3 * 24 * 60 * 60 * 1000); break;
      case 'week':     startDate = new Date(now - 7 * 24 * 60 * 60 * 1000); break;
      case 'month':    startDate = new Date(now - 30 * 24 * 60 * 60 * 1000); break;
      case '3_months': startDate = new Date(now - 90 * 24 * 60 * 60 * 1000); break;
      case '6_months': startDate = new Date(now - 180 * 24 * 60 * 60 * 1000); break;
      case 'year':     startDate = new Date(now - 365 * 24 * 60 * 60 * 1000); break;
      default: return this.schedules;
    }
    return this.schedules.filter(s => {
      const created = new Date(s.created_at);
      return created >= startDate && created <= now;
    });
  },
  },
mounted() {
    this.loadSchedules();
    this.pollTimer = setInterval(() => {
      this.loadSchedules(true);   // <-- true = silent, no loading spinner
    }, 10000);
},
beforeUnmount() {
    clearInterval(this.pollTimer);
},
  methods: {
     setDateFilter(value) {
    this.dateFilter = value;
    // no server reload needed; client‑side filter will react automatically
  },
      async loadSchedules(silent = false) {
      if (!silent) this.loading = true;
      await this.$store.dispatch('schedule/load', this.statusFilter);
      await this.$store.dispatch('schedule/loadSummary');
      if (!silent) this.loading = false;
    },
    viewSchedule(id) {
      this.$router.push({ name: 'ScheduleDetail', params: { id } });
    },
    openCreateModal() {
      this.$refs.createModal.open();
    },
    openEditModal(schedule) {
      this.$refs.createModal.open(schedule);
    },
    async confirmCancel(schedule) {
      const result = await this.$swal.fire({
        title: 'Cancel Schedule?',
        text: `This will stop "${schedule.name}". Devices currently updating will finish, but pending devices will be expired.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, cancel it',
        confirmButtonColor: '#f5365c',
      });
      if (result.isConfirmed) {
        await this.$store.dispatch('schedule/cancel', schedule.id);
        this.loadSchedules();
      }
    },
    onScheduleCreated() {
      this.loadSchedules();
      this.$store.dispatch('schedule/loadSummary');
    },
    onScheduleUpdated() {
      this.loadSchedules();
    },
    statusVariant(status) {
      return { active: 'success', completed: 'secondary', cancelled: 'danger' }[status] || 'secondary';
    },
    formatDate(iso) {
      if (!iso) return '—';
      return new Date(iso).toLocaleDateString();
    },
    async confirmDeleteSchedule(schedule) {
  const result = await this.$swal.fire({
    title: 'Delete schedule?',
    text: `This will permanently remove "${schedule.name}". This cannot be undone.`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Yes, delete it',
    confirmButtonColor: '#f5365c',
  });
  if (result.isConfirmed) {
    try {
      await this.$http.delete(
        `/tenant/${this.$store.getters.activeEntity}/schedule/${schedule.id}`
      );
      this.loadSchedules();
    } catch (err) {
      this.$swal.fire('Error', err.response?.data?.error || 'Failed to delete schedule', 'error');
    }
  }
},

async confirmCleanup() {
  const result = await this.$swal.fire({
    title: 'Delete all completed/cancelled schedules?',
    text: 'This will permanently remove every schedule that is not active. This cannot be undone.',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Yes, delete them all',
    confirmButtonColor: '#f5365c',
  });
  if (result.isConfirmed) {
    try {
      await this.$http.delete(
        `/tenant/${this.$store.getters.activeEntity}/schedule/cleanup`
      );
      this.loadSchedules();
      this.$store.dispatch('schedule/loadSummary');
    } catch (err) {
      this.$swal.fire('Error', err.response?.data?.error || 'Failed to clean up schedules', 'error');
    }
  }
},
  },
};
</script>

<style scoped>
.tab-btn {
  border: none;
  border-bottom: 3px solid transparent;
  border-radius: 0;
  color: #8898aa;
  font-weight: 500;
  padding: 0.5rem 1rem;
  background: transparent;
  transition: all 0.15s ease;
  font-size: 0.875rem;
}

.tab-btn:hover {
  color: #5e72e4;
  background: transparent;
  border-bottom-color: #e2e8f0;
}

.tab-btn.active {
  color: #5e72e4;
  border-bottom-color: #5e72e4;
  background: transparent;
}

.tab-btn .badge {
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
}

.tab-btn:focus {
  outline: none;
  box-shadow: none;
}

.card-header {
  padding: 1rem 1.25rem;
  background-color: #fff;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}
</style>